import { type I18n as LinguiI18n, type Messages, setupI18n } from '@lingui/core';
/* eslint-disable no-case-declarations */
import { nl as PLURALS_NL, fr as PLURALS_FR, en as PLURALS_EN } from 'make-plural/plurals';
import { Filters, RecordCache, SingletonCache } from '@funkey/shared';
import { messages as CATALOG_EN } from './generated/en/messages';
import { messages as CATALOG_NL_BE } from './generated/nl-BE/messages';
import { messages as CATALOG_FR_BE } from './generated/fr-BE/messages';
import { I18N_CONF, ParsedLocale } from './config';

/** @deprecated This doesn't relyably work for server components */
export const localeStore = new SingletonCache<I18N_CONF.Locale>(I18N_CONF.DEFAULT_LOCALE, {
    globalCacheID: 'i18n_locale',
});

const I18N_INSTANCES = new RecordCache<Record<I18N_CONF.Locale, I18n>>();

export interface I18n extends LinguiI18n {
    locale: I18N_CONF.Locale;
    language: I18N_CONF.Language;
    country: I18N_CONF.Country;
    parsedLocale: ParsedLocale;
}

export function getI18n(locale: I18N_CONF.Locale): I18n;
export function getI18n(locale?: string): I18n | null;
export function getI18n(locale?: string) {
    // TODO: Delete this when `localeStore` is deleted
    if (locale === undefined) {
        // eslint-disable-next-line no-param-reassign
        locale = localeStore.get();
    }

    if (!Filters.inUnion(I18N_CONF.LOCALES)(locale)) return null as any;

    const i18n: I18n = I18N_INSTANCES.getOrDefault(locale, () => {
        const parsedLocale = ParsedLocale.fromLocale(locale);

        const PLURALS = {
            en: PLURALS_EN,
            fr: PLURALS_FR,
            nl: PLURALS_NL,
        } as const satisfies Record<I18N_CONF.Language, any>;

        const i18n = setupI18n({
            locale,
            missing: (_locale, key) => `🚨${key}`,
            localeData: {
                [locale]: { plurals: PLURALS[parsedLocale.language] },
            },
            messages: { [locale]: Locale.toCatalog(locale) },
        });

        return Object.assign(i18n as I18n, {
            language: parsedLocale.language,
            country: parsedLocale.country,
            parsedLocale,
        }) satisfies I18n;
    });

    return i18n;
}

// Maybe this can be converted into a class of some sorts.
namespace Locale {
    export function toCatalog<T extends I18N_CONF.Locale>(locale: T): Messages {
        switch (locale) {
            case 'nl-BE':
            case 'nl-NL':
                return CATALOG_NL_BE;
            case 'fr-BE':
            case 'fr-LU':
                return CATALOG_FR_BE;
            case 'en-BE':
            case 'en-NL':
            case 'en-LU':
                return CATALOG_EN;
            default:
                locale satisfies never;
                throw new Error(
                    `There doesn't exist a translations catalog for locale "${locale}"`,
                );
        }
    }

    /** @deprecated Use `ParsedLocale.fromUnknownLocaleOrDefault(locale).language` instead */
    export function toDbLocale(locale: I18N_CONF.Locale | (string & {})) {
        return ParsedLocale.fromUnknownLocaleOrDefault(locale).language;
    }
}

export namespace translations {
    export const getTranslationWithFallback = (
        field: Record<string, string> & {
            [L in I18N_CONF.Language]?: string;
        },
        locale: I18N_CONF.Language | (string & {}),
    ) => field[locale] || field.en;
}

/** @deprecated Use `ParsedLocale.fromUnknownLocaleOrDefault(locale).language` instead */
// eslint-disable-next-line
export const toDbLocale = Locale.toDbLocale;
